import { AxiosError } from 'axios';
import { axiosInstance } from '../api/axios.instance';
import { API_CONFIG } from '../api/config';
import { 
  ListarComprasAgilesResponseSchema,
  transformComprasAgilesApiToUi,
  type ListarComprasAgilesUi
} from '../schemas/compras-agiles/listar-compras-agiles';
import { 
  CompraAgilDetalleResponseSchema,
  transformCompraAgilDetalleApiToUi,
  type CompraAgilDetalleUi
} from '../schemas/compras-agiles/detalle';
import { 
  type ActualizarEstadoRequest 
} from '../schemas/licitaciones/estados/api.schema';
import { useUsuarioStore } from '../store/usuarioStore';
import {
  ListarMensajesComprasAgilesResponseSchema,
  transformMensajesComprasAgilesApiToUi
} from '../schemas/compras-agiles/listar-mensajes';
import { type ListarMensajesComprasAgilesUi } from '../schemas/compras-agiles/listar-mensajes';
import {
  CrearMensajeCompraAgilRequestSchema,
  CrearMensajeCompraAgilResponseSchema,
  type CrearMensajeCompraAgilResponse
} from '../schemas/compras-agiles/crear-mensaje';

export class ComprasAgilesService {
  static async listarComprasAgiles(page: number = 1, limit: number = 20, estado?: string): Promise<{
    data: ListarComprasAgilesUi[];
    total: number;
    page: number;
    limit: number;
  }> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (estado) {
        params.append('estado', estado);
      }

      const response = await axiosInstance.get(
        `${API_CONFIG.ENDPOINTS.COMPRAS_AGILES.LISTAR}?${params.toString()}`
      );
      
      const validatedData = ListarComprasAgilesResponseSchema.parse(response.data);
      
      const transformedData = transformComprasAgilesApiToUi(validatedData.data);

      return {
        data: transformedData,
        total: validatedData.total,
        page: validatedData.page,
        limit: validatedData.limit
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('Sesión expirada o no autorizada');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al obtener las compras ágiles');
    }
  }

  static async fetchCompraAgilDetail(codigoExterno: string): Promise<CompraAgilDetalleUi> {
    try {
      const response = await axiosInstance.get(
        API_CONFIG.ENDPOINTS.COMPRAS_AGILES.DETALLE(codigoExterno)
      );
      
      const validatedData = CompraAgilDetalleResponseSchema.parse(response.data);
      
      return transformCompraAgilDetalleApiToUi(validatedData);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('Sesión expirada o no autorizada');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al obtener el detalle de la compra ágil');
    }
  }

  static async actualizarEstados(listado: ActualizarEstadoRequest['listado']): Promise<void> {
    try {
      const usuario = useUsuarioStore.getState().usuario;
      if (!usuario?.tematica?.id) {
        throw new Error('No se pudo obtener la temática del usuario. Por favor, inicia sesión nuevamente.');
      }

      const request: ActualizarEstadoRequest = {
        listado,
        tematica_id: usuario.tematica.id
      };

      await axiosInstance.post(
        API_CONFIG.ENDPOINTS.COMPRAS_AGILES.ACTUALIZAR_ESTADO,
        request
      );
    } catch (error) {
      console.error('Error en actualizarEstados:', error);
      
      if (error instanceof AxiosError) {
        console.error('Detalles del error:', {
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers
        });

        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        
        if (!error.response) {
          throw new Error('Error de red: No se pudo conectar con el servidor');
        }

        throw new Error(
          error.response.data?.detail || 
          error.response.data?.message || 
          error.message || 
          'Error al actualizar los estados'
        );
      }
      
      throw new Error('Error desconocido al actualizar los estados');
    }
  }

  static async postular(compraAgilId: string | number): Promise<{ message: string; data: any }> {
    try {
      const response = await axiosInstance.post(API_CONFIG.ENDPOINTS.COMPRAS_AGILES.POSTULAR(compraAgilId));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al postular la compra ágil');
    }
  }

  static async adjudicar(compraAgilId: string | number): Promise<{ message: string; data: any }> {
    try {
      const response = await axiosInstance.post(API_CONFIG.ENDPOINTS.COMPRAS_AGILES.ADJUDICAR(compraAgilId));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al adjudicar la compra ágil');
    }
  }

  static async listarMensajes(compraAgilId: number): Promise<ListarMensajesComprasAgilesUi> {
    try {
      const response = await axiosInstance.get(
        API_CONFIG.ENDPOINTS.COMPRAS_AGILES.MENSAJES(compraAgilId)
      );
      
      const validatedData = ListarMensajesComprasAgilesResponseSchema.parse(response.data);
      
      return transformMensajesComprasAgilesApiToUi(validatedData);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al obtener los mensajes de la compra ágil');
    }
  }

  static async crearMensaje(compraAgilId: number, mensaje: string): Promise<CrearMensajeCompraAgilResponse> {
    try {
      const requestData = CrearMensajeCompraAgilRequestSchema.parse({ mensaje });
      
      const response = await axiosInstance.post(
        API_CONFIG.ENDPOINTS.COMPRAS_AGILES.CREAR_MENSAJE(compraAgilId),
        requestData
      );
      
      return CrearMensajeCompraAgilResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al crear el mensaje');
    }
  }

  static async eliminarMensaje(mensajeId: string): Promise<void> {
    try {
      await axiosInstance.delete(API_CONFIG.ENDPOINTS.COMPRAS_AGILES.ELIMINAR_MENSAJE(mensajeId));
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al eliminar el mensaje');
    }
  }

  static async marcarComoVisto(compraAgilId: number): Promise<void> {
    try {
      await axiosInstance.post(
        API_CONFIG.ENDPOINTS.COMPRAS_AGILES.MARCAR_VISTO(compraAgilId)
      );
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('Sesión expirada o no autorizada');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      console.error('❗ Error inesperado al marcar compra ágil como vista:', error);
      throw new Error('Error desconocido al marcar la compra ágil como vista');
    }
  }
} 